import {
  isSalesQualified,
  type QuizAnswers,
  type ScaleLevel
} from "@quiz-marketing/contracts";
import {quizAnswerLabels} from "../../quiz-labels.js";

export type QuizAnswerKey = keyof QuizAnswers;

export interface QuizQuestion {
  id: QuizAnswerKey;
  prompt: string;
  instructions: string;
  options: ReadonlyArray<{ value: string; label: string }>;
}

const singleAnswerInstructions = "Selección múltiple, una sola respuesta.";

function optionsFor(answer: QuizAnswerKey): ReadonlyArray<{ value: string; label: string }> {
  return Object.entries(quizAnswerLabels[answer]).map(([value, label]) => ({ value, label }));
}

export const quizQuestions: ReadonlyArray<QuizQuestion> = [
  {
    id: "timeAllocation",
    prompt: "¿Dónde pasas la mayor parte de tu tiempo?",
    instructions: singleAnswerInstructions,
    options: optionsFor("timeAllocation")
  },
  {
    id: "dailyDependence",
    prompt: "¿Cuánto depende de ti el funcionamiento diario del negocio?",
    instructions: singleAnswerInstructions,
    options: optionsFor("dailyDependence")
  },
  {
    id: "operatingKnowledge",
    prompt: "¿Dónde está el conocimiento para operar el negocio?",
    instructions: singleAnswerInstructions,
    options: optionsFor("operatingKnowledge")
  },
  {
    id: "dailyDecisions",
    prompt: "¿Quién toma las decisiones del día a día?",
    instructions: singleAnswerInstructions,
    options: optionsFor("dailyDecisions")
  },
  {
    id: "problemHandling",
    prompt: "Cuando surge un problema importante, ¿qué suele pasar?",
    instructions: singleAnswerInstructions,
    options: optionsFor("problemHandling")
  },
  {
    id: "priorityManagement",
    prompt: "¿Cómo maneja tu empresa las prioridades?",
    instructions: singleAnswerInstructions,
    options: optionsFor("priorityManagement")
  },
  {
    id: "resultAccountability",
    prompt: "¿Qué tan claro está quién responde por cada resultado?",
    instructions: singleAnswerInstructions,
    options: optionsFor("resultAccountability")
  },
  {
    id: "resultsTracking",
    prompt: "¿Cómo dan seguimiento a los resultados?",
    instructions: singleAnswerInstructions,
    options: optionsFor("resultsTracking")
  },
  {
    id: "decisionDelegation",
    prompt: "¿Qué tan delegadas están las decisiones?",
    instructions: singleAnswerInstructions,
    options: optionsFor("decisionDelegation")
  },
  {
    id: "strategyTime",
    prompt: "¿Cuánto de tu tiempo puedes dedicar a estrategia y crecimiento?",
    instructions: singleAnswerInstructions,
    options: optionsFor("strategyTime")
  },
  {
    id: "growthCapacity",
    prompt: "Si mañana duplicaras clientes, ventas o proyectos, ¿qué pasaría?",
    instructions: singleAnswerInstructions,
    options: optionsFor("growthCapacity")
  },
  {
    id: "technologyIntegration",
    prompt: "¿Qué tan integrados están tecnología, datos, automatización e IA?",
    instructions: singleAnswerInstructions,
    options: optionsFor("technologyIntegration")
  },
  {
    id: "annualRevenue",
    prompt: "Ingresos anuales de tu negocio (en dólares - USD) *",
    instructions: singleAnswerInstructions,
    options: optionsFor("annualRevenue")
  },
  {
    id: "teamSize",
    prompt: "¿Cuántas personas trabajan en tu negocio?",
    instructions: singleAnswerInstructions,
    options: optionsFor("teamSize")
  }
];

export const standardResultPages = {
  zoila_yolo: "/resultados/nivel-soyla-o-yolo/index.html",
  pulpo: "/resultados/nivel-pulpo/index.html",
  capitan: "/resultados/nivel-capitan/index.html",
  aguila: "/resultados/nivel-aguila/index.html"
} as const satisfies Record<ScaleLevel, string>;

export const courseResultPages = {
  zoila_yolo: "/resultados/nivel-soyla-o-yolo-emp/index.html",
  pulpo: "/resultados/nivel-pulpo-emp/index.html",
  capitan: "/resultados/nivel-capitan-emp/index.html",
  aguila: "/resultados/nivel-aguila-emp/index.html"
} as const satisfies Record<ScaleLevel, string>;

export type ResultOffer = "calendar" | "course";

/** Sales-qualified prospects schedule with sales; everyone else receives the self-serve growth offer. */
export function getResultOffer(annualRevenue: QuizAnswers["annualRevenue"]): ResultOffer {
  return isSalesQualified(annualRevenue) ? "calendar" : "course";
}

export function getStandardResultPage(level: ScaleLevel): string {
  return standardResultPages[level];
}

export function getResultPage(level: ScaleLevel, annualRevenue: QuizAnswers["annualRevenue"]): string {
  return getResultOffer(annualRevenue) === "calendar"
    ? standardResultPages[level]
    : courseResultPages[level];
}

export const levelContent: Record<ScaleLevel, { title: string; description: string }> = {
  zoila_yolo: {
    title: "Soyla o Yolo",
    description: "Haces casi todo. El siguiente paso es organizar el trabajo y empezar a delegar."
  },
  pulpo: {
    title: "Pulpo",
    description: "Tienes equipo, pero la operación aún depende de ti. El siguiente paso es darle autonomía."
  },
  capitan: {
    title: "Capitán",
    description: "Diriges a otros líderes. Tu papel es marcar el rumbo y fortalecer a quienes responden por cada área."
  },
  aguila: {
    title: "Águila",
    description: "Supervisas desde la estrategia. Tus líderes se ocupan de la operación diaria."
  }
};
